import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiFileText,
  FiPlus,
  FiTrash2,
  FiDownload,
  FiEye,
  FiSearch,
  FiFilter,
  FiUpload,
  FiCalendar,
  FiUser,
  FiTag,
  FiInfo,
  FiFile,
  FiEdit3,
  FiX,
  FiCheckCircle,
  FiShield
} from 'react-icons/fi';

const Documents = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadProgress, setUploadProgress] = useState(0);

  const [documentForm, setDocumentForm] = useState({
    title: '',
    category: 'Laws',
    description: '',
    file: null
  });

  const categories = [
    { value: 'Laws', label: 'Lois et Réglementations', color: 'bg-gradient-to-r from-red-500 to-rose-600 text-white', iconBg: 'bg-red-100 dark:bg-red-900/40', iconColor: 'text-red-600 dark:text-red-400' },
    { value: 'Rules', label: 'Règles et Procédures', color: 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white', iconBg: 'bg-blue-100 dark:bg-blue-900/40', iconColor: 'text-blue-600 dark:text-blue-400' },
    { value: 'Contracts', label: 'Contrats et Formulaires', color: 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white', iconBg: 'bg-green-100 dark:bg-green-900/40', iconColor: 'text-green-600 dark:text-green-400' },
    { value: 'Insurance', label: 'Assurance et Garanties', color: 'bg-gradient-to-r from-amber-500 to-orange-600 text-white', iconBg: 'bg-yellow-100 dark:bg-yellow-900/40', iconColor: 'text-yellow-600 dark:text-yellow-400' },
    { value: 'Other', label: 'Autre', color: 'bg-gradient-to-r from-gray-500 to-slate-600 text-white', iconBg: 'bg-gray-100 dark:bg-gray-900/40', iconColor: 'text-gray-600 dark:text-gray-400' }
  ];

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/documents');
      setDocuments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Échec de la récupération des documents');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();

    // Security check: Only SuperAdmin can add documents
    if (user?.role !== 'superadmin') {
      toast.error('Accès refusé. Seuls les SuperAdmin peuvent ajouter des documents.');
      return;
    }

    if (!documentForm.file) {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }

    const formData = new FormData();
    formData.append('title', documentForm.title);
    formData.append('category', documentForm.category);
    formData.append('description', documentForm.description);
    formData.append('file', documentForm.file);

    try {
      setUploadProgress(0);
      await api.post('/api/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success('Document ajouté avec succès');
      setShowAddModal(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      console.error('Error adding document:', error);
      toast.error('Échec de l\'ajout du document');
    } finally {
      setUploadProgress(0);
    }
  };

  const handleEditDocument = (doc) => {
    // Security check: Only SuperAdmin can edit documents
    if (user?.role !== 'superadmin') {
      toast.error('Accès refusé. Seuls les SuperAdmin peuvent modifier les documents.');
      return;
    }

    setEditingDocument(doc);
    setDocumentForm({
      title: doc.title,
      category: doc.category,
      description: doc.description || '',
      file: null
    });
    setShowEditModal(true);
  };

  const handleUpdateDocument = async (e) => {
    e.preventDefault();

    // Security check: Only SuperAdmin can update documents
    if (user?.role !== 'superadmin') {
      toast.error('Accès refusé. Seuls les SuperAdmin peuvent modifier les documents.');
      return;
    }

    const formData = new FormData();
    formData.append('title', documentForm.title);
    formData.append('category', documentForm.category);
    formData.append('description', documentForm.description);
    if (documentForm.file) {
      formData.append('file', documentForm.file);
    }

    try {
      await api.put(`/api/documents/${editingDocument._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      toast.success('Document mis à jour avec succès');
      setShowEditModal(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Échec de la mise à jour du document');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    // Security check: Only SuperAdmin can delete documents
    if (user?.role !== 'superadmin') {
      toast.error('Accès refusé. Seuls les SuperAdmin peuvent supprimer les documents.');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      try {
        await api.delete(`/api/documents/${documentId}`);
        toast.success('Document supprimé avec succès');
        fetchDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Échec de la suppression du document');
      }
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await api.get(`/api/documents/${documentId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Téléchargement démarré');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Échec du téléchargement');
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      const response = await api.get(`/api/documents/${documentId}/view`, {
        responseType: 'blob',
      });

      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        toast.error('Veuillez autoriser l\'ouverture des fenêtres pop-up');
        return;
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 30000);
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Échec de l\'ouverture du document');
    }
  };

  const resetForm = () => {
    setDocumentForm({
      title: '',
      category: 'Laws',
      description: '',
      file: null
    });
    setEditingDocument(null);
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryLabel = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  const getCategoryColor = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.color : 'bg-gradient-to-r from-gray-500 to-slate-600 text-white';
  };

  const getCategoryIconBg = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.iconBg : 'bg-gray-100 dark:bg-gray-900/40';
  };

  const getCategoryIconColor = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.iconColor : 'text-gray-600 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
          <FiFile className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-primary-600" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 animate-pulse">Chargement des documents...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg sm:rounded-xl shadow-lg">
              <FiFileText className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
            </div>
            Documents PDF
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Gérez vos documents légaux, règles et contrats
          </p>
          {/* Role-based access info */}
          <div className="mt-3">
            {user?.role === 'superadmin' && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-brand-green/10 to-emerald-500/10 text-brand-green dark:text-green-400 text-sm font-medium rounded-full border border-brand-green/20">
                <FiShield className="w-4 h-4" />
                Accès complet
              </span>
            )}
            {(user?.role === 'admin' || user?.role === 'staff') && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-medium rounded-full border border-primary-200 dark:border-primary-700">
                <FiEye className="w-4 h-4" />
                Consultation uniquement
              </span>
            )}
            {user?.role === 'viewer' && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-sm font-medium rounded-full border border-gray-200 dark:border-gray-700">
                <FiEye className="w-4 h-4" />
                Lecture seule
              </span>
            )}
          </div>
        </div>
        {(user?.role === 'superadmin') && (
          <button
            onClick={() => setShowAddModal(true)}
            className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-brand-green to-emerald-600 hover:from-brand-green hover:to-emerald-700 text-white font-medium rounded-xl shadow-lg shadow-brand-green/25 hover:shadow-xl hover:shadow-brand-green/30 transition-all duration-300 hover:-translate-y-0.5"
          >
            <FiPlus className="w-5 h-5 transition-transform group-hover:rotate-90 duration-300" />
            Ajouter un Document
          </button>
        )}
      </div>

      {/* Access Level Summary Card */}
      <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/10 border border-primary-200/50 dark:border-primary-700/30 rounded-2xl p-5 backdrop-blur-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <FiInfo className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100">
              Niveaux d'Accès aux Documents
            </h3>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 bg-white/60 dark:bg-gray-800/60 rounded-xl">
                <div className="w-8 h-8 flex items-center justify-center bg-brand-green/10 rounded-lg">
                  <FiShield className="w-4 h-4 text-brand-green" />
                </div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white text-sm">SuperAdmin</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Accès complet (CRUD)</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white/60 dark:bg-gray-800/60 rounded-xl">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                  <FiEye className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white text-sm">Admin & Staff</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Consulter et télécharger</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher des documents par titre, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="pl-10 pr-8 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white appearance-none cursor-pointer min-w-[200px]"
              >
                <option value="all">Toutes les catégories</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* Active filter indicator */}
        {(searchTerm || selectedCategory !== 'all') && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">Filtres actifs:</span>
            {searchTerm && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded-full">
                <FiSearch className="w-3 h-3" />
                "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-primary-900">
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            )}
            {selectedCategory !== 'all' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded-full">
                <FiTag className="w-3 h-3" />
                {getCategoryLabel(selectedCategory)}
                <button onClick={() => setSelectedCategory('all')} className="ml-1 hover:text-primary-900">
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl flex items-center justify-center">
            <FiFileText className="h-10 w-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Aucun document trouvé
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {searchTerm || selectedCategory !== 'all'
              ? 'Essayez de modifier vos critères de recherche'
              : 'Commencez par ajouter votre premier document PDF pour votre collection'
            }
          </p>
          {user?.role === 'superadmin' && !searchTerm && selectedCategory === 'all' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-xl shadow-lg shadow-primary-500/25 transition-all duration-300"
            >
              <FiPlus className="w-5 h-5" />
              Ajouter un Document
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Results Count */}
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>{filteredDocuments.length} document{filteredDocuments.length > 1 ? 's' : ''} trouvé{filteredDocuments.length > 1 ? 's' : ''}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((doc) => (
              <div
                key={doc._id}
                className="group bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:border-primary-200 dark:hover:border-primary-700 transition-all duration-300 hover:-translate-y-1"
              >
                {/* Category Header Bar */}
                <div className={`px-4 py-2 ${getCategoryColor(doc.category)}`}>
                  <span className="text-xs font-medium opacity-90">{getCategoryLabel(doc.category)}</span>
                </div>

                <div className="p-5">
                  {/* Document Info */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${getCategoryIconBg(doc.category)} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
                      <FiFileText className={`w-6 h-6 ${getCategoryIconColor(doc.category)}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {doc.title}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1 font-mono">
                        {doc.filename}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  {doc.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4 leading-relaxed">
                      {doc.description}
                    </p>
                  )}

                  {/* Meta Info */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900/50 px-2 py-1 rounded-lg">
                      <FiCalendar className="w-3.5 h-3.5" />
                      <span>{new Date(doc.createdAt).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900/50 px-2 py-1 rounded-lg">
                      <FiUser className="w-3.5 h-3.5" />
                      <span>{doc.uploader?.name || 'Système'}</span>
                    </div>
                  </div>

                  {/* File Stats & Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">{(doc.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                      <div className="flex items-center gap-1">
                        <FiEye className="w-3 h-3" />
                        <span>{doc.viewCount || 0}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleViewDocument(doc._id)}
                        className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                        title="Voir le document"
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownloadDocument(doc._id, doc.filename)}
                        className="p-2 rounded-lg bg-brand-green/10 text-brand-green hover:bg-brand-green/20 transition-colors"
                        title="Télécharger le document"
                      >
                        <FiDownload className="w-4 h-4" />
                      </button>
                      {(user?.role === 'superadmin') && (
                        <>
                          <button
                            onClick={() => handleEditDocument(doc)}
                            className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
                            title="Modifier le document"
                          >
                            <FiEdit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc._id)}
                            className="p-2 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                            title="Supprimer le document"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add Document Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FiPlus className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    Ajouter un Nouveau Document
                  </h2>
                </div>
                <button
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleAddDocument} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Titre du Document <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={documentForm.title}
                    onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400"
                    placeholder="Ex: Code de la Route, Contrat de Location..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={documentForm.category}
                    onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white cursor-pointer"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={documentForm.description}
                    onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                    rows="3"
                    placeholder="Description optionnelle du document..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Fichier PDF <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      required
                      accept=".pdf"
                      onChange={(e) => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
                    <FiInfo className="w-3 h-3" />
                    Seuls les fichiers PDF sont acceptés. Taille maximale : 10 MB
                  </p>
                  {uploadProgress > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <span>Téléchargement en cours...</span>
                        <span className="font-medium">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-primary-500 to-brand-green h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); resetForm(); }}
                    className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-xl shadow-lg shadow-primary-500/25 transition-all duration-300 flex items-center gap-2"
                  >
                    <FiCheckCircle className="w-4 h-4" />
                    Ajouter le Document
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Document Modal */}
      {showEditModal && editingDocument && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FiEdit3 className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    Modifier le Document
                  </h2>
                </div>
                <button
                  onClick={() => { setShowEditModal(false); resetForm(); }}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleUpdateDocument} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Titre du Document <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={documentForm.title}
                    onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400"
                    placeholder="Ex: Code de la Route, Contrat de Location..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={documentForm.category}
                    onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-gray-900 dark:text-white cursor-pointer"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={documentForm.description}
                    onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                    rows="3"
                    placeholder="Description optionnelle du document..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Nouveau Fichier PDF (optionnel)
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100 cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
                    <FiInfo className="w-3 h-3" />
                    Laissez vide pour conserver le fichier actuel. Seuls les fichiers PDF sont acceptés.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); resetForm(); }}
                    className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-medium rounded-xl shadow-lg shadow-amber-500/25 transition-all duration-300 flex items-center gap-2"
                  >
                    <FiCheckCircle className="w-4 h-4" />
                    Mettre à Jour
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
